import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Loading, PageHeader, Pager } from '../App';
import { adminApi, dateTime, errorMessage, type AuditLog, type PagedResult } from '../api/admin';

export function AuditPage() {
  const [data, setData] = useState<PagedResult<AuditLog> | null>(null);
  const [action, setAction] = useState('');
  const [staffAction, setStaffAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      setData(
        await adminApi.auditLogs({
          action: staffAction || action || undefined,
          entityType: staffAction ? 'StaffBranchAssignment' : entityType || undefined,
          from: from || undefined,
          to: to || undefined,
          page,
          pageSize: 25,
        }),
      );
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [action, staffAction, entityType, from, to, page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Append-only record of sensitive changes. Each entry is written in the same transaction as the change it describes."
      />

      <Alert message={error} />

      <div className="admin-card mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-3">
            <label className="form-label small mb-1" htmlFor="audit-staff-action">
              Staff assignment events
            </label>
            <select
              id="audit-staff-action"
              className="form-select form-select-sm"
              value={staffAction}
              onChange={(e) => {
                setPage(1);
                setStaffAction(e.target.value);
              }}
            >
              <option value="">No staff event filter</option>
              <option value="Organisation.EmployeeBranchAssigned">Assignment created</option>
              <option value="Organisation.EmployeeBranchAssignmentRemoved">Assignment removed</option>
            </select>
          </div>

          <div className="col-12 col-md-3">
            <label className="form-label small mb-1" htmlFor="audit-action">
              Action contains
            </label>
            <input
              id="audit-action"
              className="form-control form-control-sm"
              value={action}
              placeholder="Admin.Approve"
              disabled={Boolean(staffAction)}
              onChange={(e) => {
                setPage(1);
                setAction(e.target.value);
              }}
            />
          </div>

          <div className="col-12 col-md-3">
            <label className="form-label small mb-1" htmlFor="audit-entity">
              Entity type
            </label>
            <select
              id="audit-entity"
              className="form-select form-select-sm"
              value={entityType}
              onChange={(e) => {
                setPage(1);
                setEntityType(e.target.value);
              }}
            >
              <option value="">All</option>
              {['Property', 'Business', 'StaffBranchAssignment', 'ListingPayment', 'ListingPlan', 'AppUser', 'PlatformSetting', 'RoomType', 'PropertyPhoto', 'PropertyReport'].map(
                (value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ),
              )}
            </select>
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label small mb-1" htmlFor="audit-from">
              From
            </label>
            <input
              id="audit-from"
              type="date"
              className="form-control form-control-sm"
              value={from}
              onChange={(e) => {
                setPage(1);
                setFrom(e.target.value);
              }}
            />
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label small mb-1" htmlFor="audit-to">
              To
            </label>
            <input
              id="audit-to"
              type="date"
              className="form-control form-control-sm"
              value={to}
              onChange={(e) => {
                setPage(1);
                setTo(e.target.value);
              }}
            />
          </div>
        </div>
      </div>

      {!data ? (
        <Loading rows={8} />
      ) : data.items.length === 0 ? (
        <div className="admin-card text-muted">Nothing recorded for these filters.</div>
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Action</th>
                  <th scope="col">Entity</th>
                  <th scope="col">Actor</th>
                  <th scope="col">Summary</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((log) => (
                  <>
                    <tr key={log.id}>
                      <td className="text-nowrap small">{dateTime(log.createdAtUtc)}</td>
                      <td>
                        <code className="small">{log.action}</code>
                      </td>
                      <td className="small">
                        {log.entityType}
                        {log.entityId && (
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {log.entityId.slice(0, 8)}…
                          </div>
                        )}
                      </td>
                      <td className="small">
                        {log.actorEmail ?? 'system'}
                        {log.actorRole && <div className="text-muted">{log.actorRole}</div>}
                      </td>
                      <td className="small text-muted">
                        {log.summary ?? '—'}
                        <AuditReferences log={log} />
                      </td>
                      <td>
                        {(log.beforeJson || log.afterJson) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-0"
                            onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                          >
                            {expanded === log.id ? 'Hide' : 'Detail'}
                          </button>
                        )}
                      </td>
                    </tr>

                    {expanded === log.id && (
                      <tr key={`${log.id}-detail`}>
                        <td colSpan={6}>
                          <div className="row g-2">
                            {log.beforeJson && (
                              <div className="col-12 col-md-6">
                                <div className="small text-muted mb-1">Before</div>
                                <code className="admin-json">{prettyJson(log.beforeJson)}</code>
                              </div>
                            )}
                            {log.afterJson && (
                              <div className="col-12 col-md-6">
                                <div className="small text-muted mb-1">After</div>
                                <code className="admin-json">{prettyJson(log.afterJson)}</code>
                              </div>
                            )}
                          </div>
                          {log.ipAddress && <div className="small text-muted mt-2">From {log.ipAddress}</div>}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          <Pager result={data} onPage={setPage} />
        </>
      )}
    </>
  );
}

function AuditReferences({ log }: { log: AuditLog }) {
  const payload = [log.afterJson, log.beforeJson]
    .filter((value): value is string => Boolean(value))
    .map((value) => {
      try {
        return JSON.parse(value) as Record<string, unknown>;
      } catch {
        return {};
      }
    })
    .reduce<Record<string, unknown>>((combined, current) => ({ ...combined, ...current }), {});

  const businessId = typeof payload.businessId === 'string' ? payload.businessId : undefined;
  const staffUserId = typeof payload.staffUserId === 'string' ? payload.staffUserId : undefined;
  const branchId = typeof payload.branchPropertyId === 'string' ? payload.branchPropertyId : undefined;
  if (!businessId && !staffUserId && !branchId) return null;

  return (
    <div className="d-flex flex-wrap gap-2 mt-1">
      {businessId && <Link to={`/businesses/${businessId}`}>Business</Link>}
      {staffUserId && <Link to={`/staff/${staffUserId}`}>Staff account</Link>}
      {branchId && <Link to={`/branches/${branchId}`}>Branch</Link>}
    </div>
  );
}

function prettyJson(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    // Audit payloads are written by the server, but a malformed one must not break the page.
    return value;
  }
}
